import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnInit,
} from '@angular/core';
import { User } from '@api/models/user.model';
import { UserService } from '@api/services/user.service';
import { ButtonComponent } from '@ui/button/button.component';
import { CardComponent } from '@ui/card/card.component';
import { TableColumn, TableComponent } from '@ui/table/table.component';
import { signal } from '@angular/core';
import { UsersModalComponent } from './users-modal/users-modal.component';
import { ModalService } from '@ui/modal/modal.service';
import { ConfirmService } from '@ui/confirm-modal/confirm.service';
import { NotificationService } from '@ui/notification/notification.service';
import { IndexResponse } from '@api/models/response.model';

@Component({
  selector: 'app-users',
  standalone: true,
  imports: [CardComponent, TableComponent, ButtonComponent],
  template: ` <div class="flex flex-col gap-4">
    <ui-card>
      <div class="flex justify-between items-center">
        <h1 class="text-lg text-slate-900">Gebruikers</h1>
        <ui-button [icon]="'add'" (onClick)="addUser()"
          >Gebruiker uitnodigen</ui-button
        >
      </div>
    </ui-card>

    <ui-table
      [columns]="columns"
      [data]="data()"
      [loading]="loading()"
      (onRowClick)="onRowClick($event)"
      (onPageChange)="onPageChange($event)"
      [showPagination]="true"
      [meta]="meta()"
    ></ui-table>
  </div>`,
  styles: `
    :host {
      display: block;
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UsersComponent implements OnInit {
  userService = inject(UserService);
  modalService = inject(ModalService);
  confirmService = inject(ConfirmService);
  notificationService = inject(NotificationService);

  columns: TableColumn[] = [
    {
      key: 'name',
      label: 'Naam',
    },
    {
      key: 'email',
      label: 'Email',
    },
    {
      key: 'is_admin',
      label: 'Admin',
      format: (value: boolean) => (value ? 'Ja' : 'Nee'),
    },
    {
      key: 'verified_at',
      label: 'Geverifieerd',
      format: (value: string) => (value ? new Date(value).toLocaleDateString('nl-NL') : 'Nee'),
    },
    {
      key: 'last_login_at',
      label: 'Laatste login',
      format: (value: string | null) => value ? new Date(value).toLocaleString('nl-NL', { dateStyle: 'short', timeStyle: 'short' }) : 'Nog niet ingelogd',
    },
    {
      key: 'created_at',
      label: 'Aangemaakt',
      format: (value: string) => new Date(value).toLocaleDateString('nl-NL'),
    },
    {
      key: 'actions',
      label: '',
      width: '200px',
      actions: [
        {
          label: 'Bewerken',
          icon: 'edit',
          variant: 'primary',
          onClick: (row) => this.editUser(row),
        },
        {
          label: 'Verwijderen',
          icon: 'delete',
          variant: 'danger',
          onClick: (row) => this.deleteUser(row),
        },
      ],
    },
  ];

  data = signal<User[]>([]);
  loading = signal(false);
  meta = signal<IndexResponse<User>['meta'] | null>(null);

  ngOnInit() {
    this.getUsers();
  }

  getUsers(page: number = 1) {
    this.loading.set(true);
    this.userService.getUsers(page).subscribe({
      next: (response) => {
        this.data.set(response.data);
        this.meta.set(response.meta);
        this.loading.set(false);
      },
      error: (error) => {
        this.notificationService.error('Er is iets fout gegaan bij het ophalen van gebruikers');
        this.loading.set(false);
      },
    });
  }

  onPageChange(page: number) {
    this.getUsers(page);
  }

  addUser() {
    let modalRef = this.modalService.open(UsersModalComponent);

    modalRef.onClose.subscribe(() => {
      this.getUsers();
    });
  }

  onRowClick(event: any) {
    console.log('onRowClick', event);
  }

  editUser(user: User) {
    let modalRef = this.modalService.open(UsersModalComponent, {
      data: {
        user: user,
      },
    });

    modalRef.onClose.subscribe(() => {
      this.getUsers();
    });
  }

  deleteUser(user: User) {
    this.confirmService
      .confirm({
        title: 'Weet je zeker?',
        message: 'Weet je zeker dat je deze gebruiker wilt verwijderen?',
      })
      .then((confirmed: boolean) => {
        if (confirmed) {
          this.userService.deleteUser(user.id).subscribe({
            next: () => {
              this.getUsers();
              this.notificationService.success('Gebruiker verwijderd');
            },
            error: (error) => {
              this.notificationService.error(
                'Er is iets fout gegaan bij het verwijderen van de gebruiker'
              );
            },
          });
        }
      });
  }
}
